import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllEventsGlobal, getAllOrganizers } from '../../services/api';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ManageEventsGlobal = () => {
    // Fetch Events and Organizers in parallel
    const { data: events = [], isLoading: isLoadingEvents } = useQuery({
        queryKey: ['admin-all-events-global'],
        queryFn: getAllEventsGlobal
    });

    const { data: organizers = [], isLoading: isLoadingOrganizers } = useQuery({
        queryKey: ['admin-all-organizers'],
        queryFn: getAllOrganizers
    });

    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [searchTerm, setSearchTerm] = useState('');

    // Sorting Logic
    const sortedEvents = useMemo(() => {
        let sortableItems = [...events];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle nested or specific keys
                if (sortConfig.key === 'organizer') {
                     const orgA = organizers.find(o => o.id === a.organizerId);
                     const orgB = organizers.find(o => o.id === b.organizerId);
                     aValue = orgA ? (orgA.firstName + ' ' + orgA.lastName) : '';
                     bValue = orgB ? (orgB.firstName + ' ' + orgB.lastName) : '';
                } else if (sortConfig.key === 'club') {
                    aValue = a.club?.name || '';
                    bValue = b.club?.name || '';
                } else {
                    // Default fallback for nulls
                    aValue = aValue || '';
                    bValue = bValue || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [events, organizers, sortConfig]);

    // Filtering Logic
    const filteredEvents = sortedEvents.filter(event => {
        const searchLower = searchTerm.toLowerCase();
        const org = organizers.find(o => o.id === event.organizerId);
        const orgName = org ? `${org.firstName} ${org.lastName}` : '';
        
        return (
            event.title?.toLowerCase().includes(searchLower) ||
            event.club?.name?.toLowerCase().includes(searchLower) ||
            orgName.toLowerCase().includes(searchLower)
        );
    });

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getOrganizerName = (id) => {
        const org = organizers.find(o => o.id === id);
        return org ? (
            <div className="flex flex-col">
                <span className="font-bold text-slate-900">{org.firstName} {org.lastName}</span>
                <span className="text-[10px] text-slate-400">{org.email}</span>
            </div>
        ) : <span className="text-slate-400 text-xs">Desconocido</span>;
    };

    const getClassNamesFor = (name) => {
        if (!sortConfig) return;
        return sortConfig.key === name ? sortConfig.direction : undefined;
    };

    if (isLoadingEvents || isLoadingOrganizers) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Eventos de la Plataforma</h1>
                    <p className="text-slate-500 font-medium italic">Visión centralizada de todos los pozos y torneos activos</p>
                </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4">
                <input 
                    type="text" 
                    placeholder="Buscar por evento, club u organizador..." 
                    className="flex-1 border-none bg-slate-50 rounded-xl px-4 py-2 focus:ring-2 focus:ring-primary-400 outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th 
                                    onClick={() => requestSort('title')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Evento {getClassNamesFor('title') === 'ascending' ? '↑' : getClassNamesFor('title') === 'descending' ? '↓' : ''}
                                </th>
                                <th 
                                    onClick={() => requestSort('organizer')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Organizador {getClassNamesFor('organizer') === 'ascending' ? '↑' : getClassNamesFor('organizer') === 'descending' ? '↓' : ''}
                                </th>
                                <th 
                                    onClick={() => requestSort('club')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Club {getClassNamesFor('club') === 'ascending' ? '↑' : getClassNamesFor('club') === 'descending' ? '↓' : ''}
                                </th>
                                <th 
                                    onClick={() => requestSort('date')}
                                    className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-700 select-none"
                                >
                                    Fecha {getClassNamesFor('date') === 'ascending' ? '↑' : getClassNamesFor('date') === 'descending' ? '↓' : ''}
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Pares</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredEvents.map((event) => (
                                <tr key={event.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-slate-900 text-sm whitespace-nowrap">{event.title}</span>
                                            <span className="text-[10px] text-primary-600 font-black uppercase tracking-widest italic">{event.levelMin} - {event.levelMax}</span>
                                            <span className="text-[10px] text-slate-400 uppercase">{event.type}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                         {getOrganizerName(event.organizerId)}
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-xs text-slate-500 font-medium italic text-balance">{event.club?.name || 'Club no def.'}</p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-slate-900 font-bold uppercase whitespace-nowrap">
                                                {event.date ? format(new Date(event.date), "EEE d MMM", { locale: es }) : 'S/F'}
                                            </span>
                                            <span className="text-[10px] text-slate-400">{event.startTime}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden max-w-[60px]">
                                                <div 
                                                    className="bg-primary-500 h-full rounded-full transition-all duration-500" 
                                                    style={{ width: `${Math.min((event.currentPairs / (event.maxPairs || 1)) * 100, 100)}%` }} 
                                                />
                                            </div>
                                            <span className="text-[10px] font-black italic">{event.currentPairs || 0}/{event.maxPairs || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all" title="Ver detalles">
                                                👁️
                                            </button>
                                            <button className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Eliminar (No implementado)">
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ManageEventsGlobal;
